import React, { createContext, useState, useRef, useEffect } from 'react';
import { Configuration, TransmissionOptions } from '../data/configuration.js';
import { CameraController, CameraViews } from '../camera-controller.js';
import { PaintConfigurator } from '../configurator/paint-configurator.js';
import { WheelConfigurator } from '../configurator/wheel-configurator.js';
import { InteriorConfigurator } from '../configurator/interior-configurator.js';
import { ExteriorConfigurator } from '../configurator/exterior-configurator.js';
import { AccessoriesConfigurator } from '../configurator/accessories-configurator.js';
import { HomeView } from './home-view.js';
import { LoadConfigurationView } from './load-configuration-view.js';
import { GearboxView } from './gearbox-view.js';
import { PaintworkView } from './paintwork-view.tsx';
import { WheelsView } from './wheels-view.js';
import { InteriorView } from './interior/interior-view.tsx';
import { ExteriorView } from './exterior/exterior-view.tsx';
import { AccessoriesView } from './accessories/accessories-view.tsx';
import { SummaryView } from './summary/summary-view.tsx';
import { SendToDealerView } from './summary/save-configuration/send-to-dealer-view.tsx';
import { ImportantInformationView } from './important-information/important-information-view.tsx';
import { VehicleMenu } from './vehicle-view.js';
import Modal from './ui/modal.tsx';
import { CloudData } from '../data/cloud-data.js';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

export enum ViewStates {
	Home,
	LoadConfiguration,
	Gearbox,
	Paintwork,
	Wheels,
	Interior,
	Exterior,
	Accessories,
	Summary,
	SendToDealer,
	ImportantInformation
}

interface AppProps {
	cameraController: CameraController;
	paintConfigurator: PaintConfigurator;
	wheelConfigurator: WheelConfigurator;
	interiorConfigurator: InteriorConfigurator;
	exteriorConfigurator: ExteriorConfigurator;
	accessoriesConfigurator: AccessoriesConfigurator;
	isMobile: boolean;
}

export type GlobalUserDetails = {
	firstName: string,
	lastName: string,
	email: string,
}

interface AppContextType {
	viewState: ViewStates;
	handleChangeView: (state: ViewStates) => void;
	cameraController: CameraController;
	restartConfiguration: () => void;
	exteriorConfigurator: ExteriorConfigurator;
	globalUserDetails: GlobalUserDetails;
	updateGlobalUserDetails: (key: string, value: string) => void;
	hoodUp: boolean;
	setHoodUp: (hoodUp: boolean) => void;
	isDrawerOpen: boolean;
	setIsDrawerOpen: (isOpen: boolean) => void;
	isMobile: boolean;
	applyConfiguration: () => void;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function App(props: AppProps) {

	// states.
	const [viewState, setViewState] = useState<ViewStates>(ViewStates.Home);
	const [globalUserDetails, setGlobalUserDetails] = useState<GlobalUserDetails>({
		firstName: '', lastName: '', email: ''
	});
	const [hoodUp, setHoodUp] = useState(false);
	const [isDrawerOpen, setIsDrawerOpen] = useState(props.isMobile);
	const previousViewState = useRef<ViewStates>(ViewStates.Home);

	// mirror of previousViewState for values read during render (refs may not be read during render).
	const [previousViewForRender, setPreviousViewForRender] = useState<number>(ViewStates.Home);

	// applies the current configuration to the vehicle.
	const applyConfiguration = () => {

		// default to automatic transmission in US.
		if (Configuration.isUsRegion()) {
			
			// eslint-disable-next-line react-hooks/immutability -- Configuration statics are the app's source of truth; this runs from handlers, never during render.
			Configuration.transmission = TransmissionOptions.Auto;
			props.interiorConfigurator.selectGearStick();
			Configuration.updateCosts();
		}

		// paint. a bespoke colour is carried as its paint type's option id, which selectBodyPaint and selectWingPaint both ignore, so
		// each region's colour is applied separately - and each region has its own, as the two may differ.
		props.paintConfigurator.selectBodyPaint(Configuration.bodyPaintColour);
		props.paintConfigurator.selectWingPaint(Configuration.wingPaintColour);
		if (Configuration.isBespokePaintColour(Configuration.bodyPaintColour) && Configuration.bespokeBodyPaintColour) {
			props.paintConfigurator.selectBespokeBodyPaint(Configuration.bespokeBodyPaintColour, CloudData.getPaintTypeForId(Configuration.bodyPaintColour));
		}
		if (Configuration.isBespokePaintColour(Configuration.wingPaintColour) && Configuration.bespokeWingPaintColour) {
			props.paintConfigurator.selectBespokeWingPaint(Configuration.bespokeWingPaintColour, CloudData.getPaintTypeForId(Configuration.wingPaintColour));
		}

		// wheels.
		props.wheelConfigurator.selectWheelStyle(Configuration.wheelStyle);
		props.wheelConfigurator.selectWheelNutOption(Configuration.wheelNutOption);
		props.wheelConfigurator.selectSpareWheelOption(Configuration.spareWheelOption);

		// interior.
		props.interiorConfigurator.selectLeatherStyle(Configuration.leatherStyle);
		props.interiorConfigurator.selectTunnelSidesStyle();
		props.interiorConfigurator.selectSeatPleatingStyle(Configuration.seatPleatingOption);
		props.interiorConfigurator.selectSeatBackStyle(Configuration.seatBackStyle);
		props.interiorConfigurator.selectEmbroideryOption(Configuration.headrestEmbroideryOption);
		props.interiorConfigurator.selectSeatStitchingStyle(Configuration.seatStitchingOption);
		props.interiorConfigurator.selectCarpetColour(Configuration.carpetColour);
		props.interiorConfigurator.selectDashboardOption(Configuration.dashboardOption);
		props.interiorConfigurator.selectSteeringWheelOption(Configuration.steeringWheelOption);
		props.interiorConfigurator.selectCentreConsoleOption(Configuration.centreConsoleOption);

		// exterior.
		props.exteriorConfigurator.selectHoodStyle(Configuration.hoodStyleOption);
		props.exteriorConfigurator.selectWingBeadingOption(Configuration.wingBeadingOption);
		props.exteriorConfigurator.selectStoneGuardStyle(Configuration.stoneGuardOption);
		props.exteriorConfigurator.selectGrilleMeshOption(Configuration.grilleMeshOption);
		props.exteriorConfigurator.selectSplitterBodyPaint(Configuration.bodyPaintColour);
		props.exteriorConfigurator.selectSplitterWingPaint(Configuration.wingPaintColour);
		props.exteriorConfigurator.selectSplitterStyle(Configuration.splitterOption);
		props.exteriorConfigurator.selectLicensePlateByRegion();
		props.exteriorConfigurator.selectDoorGraphicOption(Configuration.doorGraphicOption);
		props.exteriorConfigurator.selectAccentStyle(Configuration.accentPackOption);
		props.exteriorConfigurator.selectCallSigns(Configuration.callSignsFlagOption);
		props.exteriorConfigurator.selectCallSignsText('pilot', Configuration.pilotValue);
		props.exteriorConfigurator.selectCallSignsText('copilot', Configuration.copilotValue);
		props.exteriorConfigurator.selectUsFuelCap();

		// accessories.
		Array.from(Configuration.accessoryStorageOptions).map((id) => {
			props.accessoriesConfigurator.selectStorageAccessory(id);
		});
		Array.from(Configuration.accessoryEquipmentOptions).map((id) => {
			props.accessoriesConfigurator.selectEquipmentAccessory(id);
		});
		Array.from(Configuration.accessoryAftermarketOptions).map((id) => {
			props.accessoriesConfigurator.selectAftermarketAccessory(id);
		});
		props.accessoriesConfigurator.selectAudioAccessory(Configuration.audioOption);
	};

	// handlers.

	const handleConfigurationDataLoaded = () => {

		// apply the configuration to the vehicle.
		applyConfiguration();

		// if the prev page showed the vehicle then return to it.
		if (
			previousViewState.current !== ViewStates.Home &&
				previousViewState.current !== ViewStates.LoadConfiguration &&
				previousViewState.current !== ViewStates.Gearbox &&
				previousViewState.current !== ViewStates.Summary &&
				previousViewState.current !== ViewStates.SendToDealer &&
				previousViewState.current !== ViewStates.ImportantInformation
		) {
			// show the previous view state.
			setViewState(previousViewState.current);
		} else {
			// show the body paint view.
			setViewState(ViewStates.Paintwork);
		}

		showCanvas(true);
	};

	const handleChangeView = (state: ViewStates) => {

		// store the previous view state, so we can return to it or display specific UI based on the previous screen.
		previousViewState.current = viewState;

		// mirror into state, so views can react to the previous screen during render.
		setPreviousViewForRender(previousViewState.current);

		setViewState(state);

		// show the canvas on all pages, apart from the following:
		if (state === ViewStates.Home ||
			state === ViewStates.LoadConfiguration ||
			state === ViewStates.Gearbox ||
			state === ViewStates.Summary ||
			state === ViewStates.SendToDealer ||
			state === ViewStates.ImportantInformation) {
			showCanvas(false);
		}
		else {
			showCanvas(true);
		}
	};

	/*
		clears every selection, re-prices, resets the vehicle and returns to the home screen. this lives here
		rather than in a view because it needs both applyConfiguration and the accessories configurator, and is
		reached from the restart control on the vehicle panel (see RestartOptions).
	*/
	const restartConfiguration = () => {
		Configuration.resetConfiguration();
		Configuration.updateCosts();
		props.accessoriesConfigurator.resetAccessories();
		applyConfiguration();
		handleChangeView(ViewStates.Home);
	};

	const handleStartConfigurationButtonClicked = () => {
		window.scrollTo(0, 1);

		if (!props.isMobile) {
			props.cameraController.updateDesktopCameraFov();
		}

		// apply the (default) configuration to the vehicle.
		applyConfiguration();

		// if the auto-start debug option has been set, start on the specified view.
		if (Configuration.debugOptions.autoStart) {
			handleChangeView(Configuration.debugOptions.autoStartView);
		}

		// if in US region, show the paintwork view (as we skip gearbox selection, setting it to automatic in applyConfiguration).
		else if (Configuration.isUsRegion()) {
			handleChangeView(ViewStates.Paintwork);
		}

		// otherwise, show the gearbox view.
		else {
			handleChangeView(ViewStates.Gearbox);
		}
	};

	// returns the relevant view, based on the current state.
	const currentView = () => {
		switch (viewState) {
			case ViewStates.Home:

				// set the camera to the accent pack viewpoint, so we get a nice transition when selecting the subsequent configuration pages.
				props.cameraController.changeCameraView(CameraViews.AccentPackView, undefined, true);

				// present the home view.
				return (
					<HomeView
						onStartConfigurationButtonClicked={handleStartConfigurationButtonClicked}
						onLoadConfigurationButtonClicked={() => {
							handleChangeView(ViewStates.LoadConfiguration);
						}}
					/>
				);

			case ViewStates.LoadConfiguration:

				// present the load configuration view.
				return (
					<LoadConfigurationView
						onConfigurationDataLoaded={handleConfigurationDataLoaded}
						onBackButtonClicked={() => {
							handleChangeView(previousViewState.current as ViewStates);
						}}
					/>
				);

			case ViewStates.Gearbox:

				if (Configuration.isUsRegion()) {
					setViewState(ViewStates.Paintwork);
					return null;
				}

				// present the gearbox view.
				return (
					<GearboxView
						interiorConfigurator={props.interiorConfigurator}
						onBackButtonClicked={() => {
							handleChangeView(ViewStates.Home);
						}}
						onGearboxSelected={() => {
							handleChangeView(ViewStates.Paintwork);
						}}
					/>
				);

			case ViewStates.Paintwork:

				// transition the camera to the relevant viewpoint.
				props.cameraController.changeCameraView(CameraViews.ModelView);

				// present the body paint view.
				return (
					<PaintworkView
						paintConfigurator={props.paintConfigurator}
						interiorConfigurator={props.interiorConfigurator}
						exteriorConfigurator={props.exteriorConfigurator}
						onBackButtonClicked={() => {

							// if in US region, return to home instead of gearbox selection, as we default to automatic transmission.
							if (Configuration.isUsRegion()) {
								handleChangeView(ViewStates.Home)
							}
							else {
								handleChangeView(ViewStates.Gearbox);
							}
						}}
						onNextButtonClicked={() => {
							handleChangeView(ViewStates.Wheels);
						}}
					/>
				);

			case ViewStates.Wheels:

				// transition the camera to the relevant viewpoint.
				props.cameraController.changeCameraView(CameraViews.LeftWheelView);

				// present the wheels view.
				return (
					<WheelsView
						wheelConfigurator={props.wheelConfigurator}
						accessoriesConfigurator={props.accessoriesConfigurator}
						cameraController={props.cameraController}
						softTopDownLight={props.exteriorConfigurator.softTopDownLight}
						softTopUpLight={props.exteriorConfigurator.softTopUpLight}
						onBackButtonClicked={() => {
							handleChangeView(ViewStates.Paintwork);
						}}
						onNextButtonClicked={() => {
							handleChangeView(ViewStates.Interior);
						}}
					/>
				);

			case ViewStates.Interior:

				// n.b. camera transitions are handled within the interior view component.

				// present the interior view.
				return (
					<InteriorView
						cameraController={props.cameraController}
						interiorConfigurator={props.interiorConfigurator}
						onBackButtonClicked={() => {
							handleChangeView(ViewStates.Wheels);
						}}
						onNextButtonClicked={() => {
							handleChangeView(ViewStates.Exterior);
						}}
					/>
				);

			case ViewStates.Exterior:

				// n.b. camera transitions are handled within the interior view component.

				// present the exterior view.
				return (
					<ExteriorView
						cameraController={props.cameraController}
						exteriorConfigurator={props.exteriorConfigurator}
						onBackButtonClicked={() => {
							handleChangeView(ViewStates.Interior);
						}}
						onNextButtonClicked={() => {
							handleChangeView(ViewStates.Accessories);
						}}
					/>
				);

			case ViewStates.Accessories:

				// n.b. camera transitions are handled within the accessories view component.

				// present the accessories view.
				return (
					<AccessoriesView
						accessoriesConfigurator={props.accessoriesConfigurator}
						cameraController={props.cameraController}
						onBackButtonClicked={() => {
							handleChangeView(ViewStates.Exterior);
						}}
						onNextButtonClicked={() => {
							handleChangeView(ViewStates.Summary);
						}}
					/>
				);

			case ViewStates.Summary:

				// present the summary view.
				return (
					<SummaryView
						cameraController={props.cameraController}
						onBackButtonClicked={() => {
							props.cameraController.changeCameraView(CameraViews.ModelView);
							handleChangeView(ViewStates.Accessories);
						}}
						startOnStepTwo={previousViewForRender === ViewStates.SendToDealer}
						isMobile={props.isMobile}
					/>
				);

			case ViewStates.SendToDealer:

				// present the save configuration view.
				return (
					<SendToDealerView
						cameraController={props.cameraController}
						onBackButtonClicked={() => {
							handleChangeView(previousViewState.current as ViewStates);
						}}
						showHeader={true}
						onComplete={() => handleChangeView(ViewStates.Summary)}
					/>
				);

			case ViewStates.ImportantInformation:

				// present the important information view.
				return (
					<ImportantInformationView
						onBackButtonClicked={() => {
							handleChangeView(previousViewState.current as ViewStates);
						}}
					/>
				);

			default:
				return null;
		}
	};

	const updateGlobalUserDetails = (key: string, value: string) => {
		setGlobalUserDetails(prevState => ({
			...prevState,
			[key]: value
		}));
	}

	// stop menu from showing in home and gearbox views.
	const shouldHideMenu = () => {
		return viewState === ViewStates.Home ||
			viewState === ViewStates.Gearbox ||
			viewState === ViewStates.ImportantInformation ||
			viewState === ViewStates.LoadConfiguration ||
			viewState === ViewStates.SendToDealer;
	}

	// if shortcode is in URL then extract it and load the configuration.
	useEffect(() => {
		function extractShortcode(urlString: string) {
			try {
				const url = new URL(urlString);
				return url.searchParams.get('shortcode');
			} catch (error) {
				console.error('Invalid URL:', error);
				return null;
			}
		}

		const loadConfiguration = async (shortcode: string) => {
			await CloudData.loadConfiguration(shortcode);
			handleConfigurationDataLoaded();
		}

		const shortcode = extractShortcode(window.location.href);
		if (shortcode) {
			loadConfiguration(shortcode);
		}

		// run once on mount only, to read the shortcode from the URL; handleConfigurationDataLoaded is intentionally excluded from the deps.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// return the HTML.
	return (
		<AppContext.Provider
			value={{
				viewState,
				handleChangeView,
				cameraController: props.cameraController,
				restartConfiguration,
				exteriorConfigurator: props.exteriorConfigurator,
				updateGlobalUserDetails,
				globalUserDetails,
				hoodUp,
				setHoodUp,
				isDrawerOpen,
				setIsDrawerOpen,
				isMobile: props.isMobile,
				applyConfiguration
			}}>
			{currentView()}
			{!shouldHideMenu() && (
				<React.Fragment>
					<VehicleMenu />
					{viewState !== ViewStates.Summary && <img id="desktop-logo" src="assets/images/logo/morgan-full-logo-white.svg" width="180" height="auto" alt="Morgan Motor Company" />}

					{previousViewForRender === ViewStates.Gearbox && (
						<div className="tablet-modal-container">
							<Modal
								content={[{
									title: 'Welcome to the Beta Configurator',
									body: "This beta version is currently optimised for mobile devices. If you're seeing this message, please rotate your device to portrait to configure."
								}]}
								centreTitle={true}
							/>
						</div>
					)}
				</React.Fragment>
			)}

		</AppContext.Provider> 
	);
}

// context to allow any view in the hierarchy to call the change view handler, allowing the hamburger menu to work.
export const AppContext = createContext<AppContextType | undefined>(undefined);

export function showCanvas(shouldShow: boolean) {

	// add or remove the 'hide' class from the canvas element.
	const canvasElement = document.querySelector('#canvas') as HTMLElement;
	if (shouldShow) {
		canvasElement.classList.remove('hide');
	}
	else {
		canvasElement.classList.add('hide');
	}
}
